import { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { estimateResolvedLength, hasPlaceholders } from '../placeholderEstimate';
import { PlaceholderHint } from './PlaceholderHint';

// Meta primary text. Spec §2.2: 1-5 entries, multi-line, emojis + wildcards.
// Chunk F amendment: 3000-char sanity cap stays; we also surface the
// resolved-length estimate for templated entries so authors see the
// difference between authored and rendered length when {gymInfo} etc. expand.
const MAX_ENTRIES = 5;
const SANITY_CAP = 3000;

export type BodyCopyArrayEditorProps = {
  values: string[];
  onChange: (next: string[]) => void;
};

export function BodyCopyArrayEditor({ values, onChange }: BodyCopyArrayEditorProps) {
  const [justCopied, setJustCopied] = useState<number | null>(null);

  const updateAt = (index: number, value: string) => {
    const next = [...values];
    next[index] = value;
    onChange(next);
  };
  const removeAt = (index: number) => {
    onChange(values.filter((_, i) => i !== index));
  };
  const addEntry = () => {
    if (values.length >= MAX_ENTRIES) return;
    onChange([...values, '']);
  };
  const copy = async (index: number) => {
    const text = values[index] ?? '';
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setJustCopied(index);
      window.setTimeout(() => setJustCopied((c) => (c === index ? null : c)), 1200);
    } catch {
      // Clipboard API can fail (insecure context, permission denied). Silent
      // fail -- the text is visible in the textarea.
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between">
        <label className="text-sm font-medium text-slate-900">Body copy</label>
        <span className="text-xs text-slate-500">
          {values.length} / {MAX_ENTRIES} entries
        </span>
      </div>

      {values.length === 0 && (
        <p className="text-xs text-slate-500 italic">
          No body copy yet. Add at least one entry before saving.
        </p>
      )}

      {values.map((value, index) => {
        const authored = value.length;
        const estimated = estimateResolvedLength(value);
        const showEstimate = hasPlaceholders(value);
        const overSanity = authored > SANITY_CAP;
        return (
          <div key={index} className="border border-slate-200 rounded-md p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-slate-600">Entry {index + 1}</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="text-xs text-slate-500 hover:text-slate-900 disabled:opacity-50"
                  onClick={() => copy(index)}
                  disabled={!value}
                >
                  {justCopied === index ? 'Copied!' : 'Copy authored text'}
                </button>
                <button
                  type="button"
                  className="text-xs text-red-600 hover:text-red-800"
                  onClick={() => removeAt(index)}
                >
                  Remove
                </button>
              </div>
            </div>
            <textarea
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono whitespace-pre-wrap min-h-[120px] focus:outline-none focus:ring-2 focus:ring-slate-400"
              value={value}
              onChange={(e) => updateAt(index, e.target.value)}
              placeholder="Long-form body copy. Emojis and line breaks are preserved."
            />
            <div className={`text-xs ${overSanity ? 'text-red-600' : 'text-slate-500'}`}>
              {authored} chars
              {showEstimate && ` (~${estimated} resolved)`}
              {overSanity ? ` — over sanity cap ${SANITY_CAP}, will reject on save` : ''}
            </div>
          </div>
        );
      })}

      <div className="flex justify-between items-center">
        <PlaceholderHint />
        <Button
          type="button"
          variant="outline"
          onClick={addEntry}
          disabled={values.length >= MAX_ENTRIES}
        >
          Add body copy
        </Button>
      </div>
    </div>
  );
}
